/**
 * useAnalyticsIdentify — enriquece la identidad de PostHog con propiedades
 * de segmentación una vez que el usuario está logueado.
 *
 * `auth.ts` ya hace un identify mínimo (solo user_id) al cambiar la sesión,
 * para establecer identidad rápido. Este hook lo COMPLEMENTA: cuando cargan
 * el rol primario, el plan y el país, vuelve a llamar identify() con esas
 * props para poder segmentar los funnels (Free vs Premium, guru vs user,
 * por país) sin enviar PII.
 *
 * Llamar UNA SOLA VEZ en App.tsx, junto a useAuthListener().
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import { usePrimaryRole } from '@/lib/hooks/useUserRoles';
import { useMyProfile } from '@/lib/api/account';
import { identify } from '@/lib/analytics';

/**
 * plan_code activo del usuario (o 'free' si no tiene suscripción activa).
 * subscriptions es self-read por RLS, así que el cliente puede leer la suya.
 */
function useMyPlanCode() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-plan-code', user?.id],
    queryFn: async (): Promise<string> => {
      if (!user) return 'free';
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan_code, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .limit(1);
      if (error) throw error;
      return (data?.[0]?.plan_code as string | undefined) ?? 'free';
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsIdentify(): void {
  const user = useUser();
  const role = usePrimaryRole();
  const { data: profile } = useMyProfile();
  const { data: planCode } = useMyPlanCode();

  // Evita reenviar identify si nada cambió.
  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      lastSignature.current = null;
      return;
    }
    // Espera a que el rol primario esté resuelto (no null) antes de mandar,
    // para no pisar con props incompletas en el primer render.
    if (role === null) return;

    const props = {
      role,
      plan_code: planCode ?? 'free',
      country_code: profile?.country_code ?? null,
      signed_up_at: user.created_at ?? null,
    };

    const signature = JSON.stringify([user.id, props]);
    if (signature === lastSignature.current) return;
    lastSignature.current = signature;

    identify(user.id, props);
  }, [user, role, planCode, profile?.country_code]);
}
